import { useState } from 'react'
import styled from 'styled-components'
import type { ImbalanceDetails } from 'types/match'
import Spinner from 'components/Spinner'

type Props = { loadDetails: () => Promise<ImbalanceDetails | null> }
type ImbalanceKind = 'shortage' | 'surplus' | 'balanced'

const MONTH_NAMES = [
  'Styczeń',
  'Luty',
  'Marzec',
  'Kwiecień',
  'Maj',
  'Czerwiec',
  'Lipiec',
  'Sierpień',
  'Wrzesień',
  'Październik',
  'Listopad',
  'Grudzień'
]
const HEATMAP_HOUR_TICKS = Array.from({ length: 24 }, (_, index) => index + 1)
const POLISH_WEEKDAYS = ['nd', 'pon', 'wt', 'śr', 'czw', 'pt', 'sob']

export type DistributionBin = { from: number; to: number; count: number }

export function buildDistribution(values: number[], binCount = 10): DistributionBin[] {
  if (!values.length) return []
  const min = Math.min(...values)
  const max = Math.max(...values)
  if (min === max) return [{ from: min, to: max, count: values.length }]
  const width = (max - min) / binCount
  const bins = Array.from({ length: binCount }, (_, index) => ({
    from: min + index * width,
    to: index === binCount - 1 ? max : min + (index + 1) * width,
    count: 0
  }))
  values.forEach(value => bins[Math.min(Math.floor((value - min) / width), binCount - 1)].count++)
  return bins
}

function signedImbalance(slot: { shortage: number; surplus: number }) {
  if (slot.shortage > 0) return { value: -slot.shortage, kind: 'shortage' as const }
  if (slot.surplus > 0) return { value: slot.surplus, kind: 'surplus' as const }
  return { value: 0, kind: 'balanced' as const }
}

function isMonday(date: string) {
  return new Date(`${date}T00:00:00Z`).getUTCDay() === 1
}

function shortWeekday(date: string) {
  return POLISH_WEEKDAYS[new Date(`${date}T00:00:00Z`).getUTCDay()]
}

function dayMonth(date: string) {
  return `${date.slice(8, 10)}.${date.slice(5, 7)}`
}

export default function ImbalanceVisualization({ loadDetails }: Props) {
  const [details, setDetails] = useState<ImbalanceDetails | null>(null)
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const show = async () => {
    setOpen(true)
    if (details || loading) return
    setLoading(true)
    setError(false)
    try {
      const result = await loadDetails()
      setDetails(result)
      setError(!result)
    } catch {
      setError(true)
    } finally {
      setLoading(false)
    }
  }

  const days = details?.days ?? []
  const slots = days.flatMap(day => day.hours.map(hour => ({ ...hour, date: day.date })))
  const months = MONTH_NAMES.map((name, index) => ({
    name,
    days: days.filter(day => Number(day.date.slice(5, 7)) === index + 1)
  })).filter(month => month.days.length)
  const signed = slots.map(signedImbalance)
  const bins: (DistributionBin & { kind: ImbalanceKind })[] = [
    ...buildDistribution(
      signed.filter(item => item.kind === 'shortage').map(item => item.value)
    ).map(bin => ({ ...bin, kind: 'shortage' as const })),
    ...(signed.some(item => item.kind === 'balanced')
      ? [
          {
            from: 0,
            to: 0,
            count: signed.filter(item => item.kind === 'balanced').length,
            kind: 'balanced' as const
          }
        ]
      : []),
    ...buildDistribution(
      signed.filter(item => item.kind === 'surplus').map(item => item.value)
    ).map(bin => ({ ...bin, kind: 'surplus' as const }))
  ]
  const shortageMax = Math.max(
    0,
    ...signed.filter(item => item.kind === 'shortage').map(item => Math.abs(item.value))
  )
  const surplusMax = Math.max(
    0,
    ...signed.filter(item => item.kind === 'surplus').map(item => item.value)
  )

  return (
    <>
      <Action onClick={show}>Pokaż profil niedoborów i nadwyżek</Action>
      {open && (
        <Panel role="dialog" aria-label="Profil niedoborów i nadwyżek">
          <Close onClick={() => setOpen(false)} aria-label="Zamknij">
            ×
          </Close>
          {loading && (
            <>
              <Spinner size={20} /> Ładowanie…
            </>
          )}
          {error && (
            <p>
              Nie udało się pobrać danych. <button onClick={show}>Spróbuj ponownie</button>
            </p>
          )}
          {!loading && !error && details && slots.length === 0 && (
            <p>Brak danych do wyświetlenia.</p>
          )}
          {!!slots.length && (
            <>
              <h3>Mapa godzinowa</h3>
              <Legend>
                <span className="shortage">Niedobór</span>
                <span>Bilans</span>
                <span className="surplus">Nadwyżka</span>
              </Legend>
              <MonthlyHeatmaps>
                {months.map(month => {
                  const monthSlots = month.days.flatMap(day =>
                    day.hours.map(hour => ({ ...hour, date: day.date }))
                  )
                  return (
                    <MonthPanel key={month.name} aria-label={`Mapa: ${month.name}`}>
                      <MonthHeading>{month.name}</MonthHeading>
                      <HeatmapXAxis aria-label={`Oś X mapy ${month.name}: godzina doby`}>
                        {HEATMAP_HOUR_TICKS.map(hour => (
                          <span key={hour} style={{ left: (hour - 1) * 19 + 9 }}>
                            {hour}
                          </span>
                        ))}
                      </HeatmapXAxis>
                      <HeatmapYAxis
                        aria-label={`Lewa oś Y mapy ${month.name}: daty poniedziałków`}
                        $rows={month.days.length}
                      >
                        {month.days.map((day, index) =>
                          isMonday(day.date) ? (
                            <span key={day.date} style={{ top: index * 19 }}>
                              {dayMonth(day.date)}
                            </span>
                          ) : null
                        )}
                      </HeatmapYAxis>
                      <Heatmap aria-label={`Godzinowa mapa: ${month.name}`}>
                        {monthSlots.map(slot => {
                          const { value } = signedImbalance(slot)
                          const threshold = value < 0 ? shortageMax : surplusMax
                          const alpha = threshold ? 0.15 + (0.85 * Math.abs(value)) / threshold : 0
                          const category =
                            value < 0 ? 'Niedobór' : value > 0 ? 'Nadwyżka' : 'Bilans'
                          const label = `${slot.date}, ${String(slot.hour).padStart(
                            2,
                            '0'
                          )}:00, ${category}, niedobór ${slot.shortage} MWh, nadwyżka ${
                            slot.surplus
                          } MWh`
                          return (
                            <Cell
                              key={`${slot.date}-${slot.hour}`}
                              tabIndex={0}
                              aria-label={label}
                              title={label}
                              style={{
                                background:
                                  value < 0
                                    ? `rgba(35, 90, 190, ${alpha})`
                                    : value > 0
                                    ? `rgba(190, 35, 35, ${alpha})`
                                    : '#eee'
                              }}
                            />
                          )
                        })}
                      </Heatmap>
                      <WeekdayYAxis
                        aria-label={`Prawa oś Y mapy ${month.name}: dni tygodnia`}
                        $rows={month.days.length}
                      >
                        {month.days.map((day, index) => (
                          <span key={day.date} style={{ top: index * 19 }}>
                            {shortWeekday(day.date)}
                          </span>
                        ))}
                      </WeekdayYAxis>
                    </MonthPanel>
                  )
                })}
              </MonthlyHeatmaps>
              <h3>Rozkład godzinowy</h3>
              <HistogramFigure>
                <HistogramYAxis aria-label="Oś Y rozkładu: procent godzin">
                  {[100, 75, 50, 25, 0].map(value => (
                    <span key={value}>{value}%</span>
                  ))}
                </HistogramYAxis>
                <Histogram aria-label="Rozkład godzin według wielkości niedoboru i nadwyżki">
                  <ZeroLine role="separator" aria-label="Wartość zero">
                    <span>0</span>
                  </ZeroLine>
                  {bins.map((bin, index) => {
                    const percentage = slots.length ? (bin.count / slots.length) * 100 : 0
                    const threshold = bin.kind === 'shortage' ? shortageMax : surplusMax
                    const magnitude = Math.max(Math.abs(bin.from), Math.abs(bin.to))
                    const intensity =
                      bin.kind === 'balanced' || !threshold
                        ? 1
                        : 0.15 + (0.85 * magnitude) / threshold
                    const label = `${bin.from.toFixed(2)}–${bin.to.toFixed(2)} MWh: ${
                      bin.count
                    } godzin (${percentage.toFixed(1)}%)`
                    return (
                      <Bar
                        key={index}
                        aria-label={label}
                        title={label}
                        $height={Math.max(2, percentage)}
                        $kind={bin.kind}
                        $intensity={intensity}
                      >
                        <span>{bin.count}</span>
                      </Bar>
                    )
                  })}
                </Histogram>
                <HistogramXAxis aria-label="Oś X rozkładu: bilans energii w MWh">
                  {bins.map((bin, index) => (
                    <span key={index}>{((bin.from + bin.to) / 2).toFixed(1)}</span>
                  ))}
                </HistogramXAxis>
                <HistogramXTitle>Niedobór ← bilans energii (MWh) → nadwyżka</HistogramXTitle>
                <HistogramYTitle>Udział godzin</HistogramYTitle>
              </HistogramFigure>
            </>
          )}
        </Panel>
      )}
    </>
  )
}

const Action = styled.button`
  background: none;
  border: 0;
  padding: 0;
  color: var(--green);
  cursor: pointer;
  font: var(--400-13);
  margin-left: 12px;
`
const Panel = styled.section`
  position: fixed;
  inset: 5%;
  z-index: 20;
  background: white;
  border: 1px solid var(--green);
  border-radius: 4px;
  padding: 24px;
  overflow: auto;
  box-shadow: 0 4px 24px #0004;
`
const Close = styled.button`
  float: right;
  font-size: 24px;
  background: none;
  border: 0;
  cursor: pointer;
`
const Legend = styled.div`
  display: flex;
  gap: 16px;
  margin: 8px 0;
  font-size: 12px;
  .shortage {
    color: #235abe;
  }
  .surplus {
    color: #be2323;
  }
`
const MonthlyHeatmaps = styled.div`
  display: grid;
  grid-template-rows: repeat(4, auto);
  grid-auto-flow: column;
  grid-auto-columns: max-content;
  gap: 28px 2px;
  width: max-content;
  margin-top: 12px;
`
const MonthPanel = styled.section`
  display: grid;
  grid-template-columns: 54px auto 34px;
  grid-template-rows: 22px 18px auto;
  grid-template-areas:
    '. heading .'
    '. x-axis .'
    'left-axis plot right-axis';
  width: max-content;
`
const MonthHeading = styled.h4`
  grid-area: heading;
  margin: 0;
  text-align: center;
`
const HeatmapXAxis = styled.div`
  grid-area: x-axis;
  position: relative;
  width: 455px;
  font-size: 9px;
  color: var(--gray);
  span {
    position: absolute;
    transform: translateX(-50%);
    text-align: center;
  }
`
const HeatmapYAxis = styled.div<{ $rows: number }>`
  grid-area: left-axis;
  position: relative;
  height: ${p => Math.max(18, p.$rows * 19 - 1)}px;
  font-size: 9px;
  color: var(--gray);
  span {
    position: absolute;
    right: 6px;
  }
`
const WeekdayYAxis = styled.div<{ $rows: number }>`
  grid-area: right-axis;
  position: relative;
  height: ${p => Math.max(18, p.$rows * 19 - 1)}px;
  font-size: 9px;
  color: var(--gray);
  span {
    position: absolute;
    left: 6px;
  }
`
const Heatmap = styled.div`
  grid-area: plot;
  display: grid;
  grid-template-columns: repeat(24, 18px);
  grid-auto-rows: 18px;
  gap: 1px;
  width: 455px;
`
const Cell = styled.div`
  outline-offset: 1px;
`
const HistogramFigure = styled.div`
  display: grid;
  grid-template-columns: 46px minmax(420px, 1fr);
  grid-template-rows: 220px 44px 20px;
  grid-template-areas:
    'y-axis plot'
    '. x-axis'
    'y-title x-title';
  min-width: 500px;
  margin-top: 28px;
`
const HistogramYAxis = styled.div`
  grid-area: y-axis;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;
  padding-right: 7px;
  font-size: 10px;
  color: var(--gray);
`
const Histogram = styled.div`
  grid-area: plot;
  display: flex;
  align-items: flex-end;
  gap: 4px;
  border-left: 1px solid var(--gray);
  border-bottom: 1px solid var(--gray);
  background: repeating-linear-gradient(to bottom, transparent 0, transparent 54px, #ddd 55px);
  padding: 0 4px;
  position: relative;
`
const ZeroLine = styled.div`
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  left: 50%;
  border-left: 2px solid var(--text);
  pointer-events: none;
  span {
    position: absolute;
    top: 100%;
    left: 0;
    transform: translate(-50%, 3px);
    padding: 0 2px;
    background: white;
    font-size: 9px;
  }
`
const HistogramXAxis = styled.div`
  grid-area: x-axis;
  display: flex;
  gap: 4px;
  padding: 3px 4px 0;
  span {
    flex: 1;
    min-width: 0;
    font-size: 8px;
    text-align: center;
    transform: rotate(-45deg);
  }
`
const HistogramXTitle = styled.div`
  grid-area: x-title;
  text-align: center;
  font-size: 10px;
`
const HistogramYTitle = styled.div`
  grid-area: y-title;
  font-size: 10px;
`
const Bar = styled.div<{ $height: number; $kind: ImbalanceKind; $intensity: number }>`
  height: ${p => p.$height}%;
  min-height: 2px;
  flex: 1;
  background: ${p =>
    p.$kind === 'shortage'
      ? `rgba(35, 90, 190, ${p.$intensity})`
      : p.$kind === 'surplus'
      ? `rgba(190, 35, 35, ${p.$intensity})`
      : '#888'};
  position: relative;
  span {
    position: absolute;
    bottom: 100%;
    font-size: 10px;
  }
`
